import { Image, type InputRef, Table } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { pick } from 'lodash/fp';
import { useCallback, useMemo, useRef, type Key } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { type BannerListDataFragment } from '../../../api/fragments/BannerListData';
import { useListBannersQuery } from '../../../api/queries/listBanners';
import { BannerSortingField, CompanyFilterListCollection, type UploadedFileWithPreview } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import SearchBox from '../../../components/SearchBox';
import { allowedExtensions } from '../../../utilities/extensions';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';

const StyledImage = styled(Image)`
    .ant-image-preview-operations-operation:not(:first-child) {
        display: none;
    }

    .ant-image-mask {
        display: none;
    }

    aspect-ratio: 10/3;
    width: 100%;
    max-width: 22rem;
    object-fit: cover;
`;

const StyledVideo = styled.video`
    width: 100%;
    max-width: 22rem;
    object-fit: cover;
    aspect-ratio: 10/3;
`;

const getSortField = makeGetSortingRule((field): BannerSortingField => {
    switch (field) {
        case 'module.company.displayName':
            return BannerSortingField.Company;

        case 'module.displayName':
            return BannerSortingField.ModuleName;

        case 'bannerText.defaultValue':
            return BannerSortingField.BannerText;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});

type BannerDataSource = BannerListDataFragment & { key: Key };

type PaginatedBannerTable = PaginatedTableProps<BannerDataSource>;

const renderBannerPreview = (t: TFunction, file: UploadedFileWithPreview) => {
    const fileExt = file?.filename.split('.').pop();
    const isVideo = allowedExtensions.video.find(videoExt => videoExt.includes(fileExt));
    const isImage = allowedExtensions.image.find(imageExt => imageExt.includes(fileExt));

    if (isVideo) {
        return (
            <StyledVideo autoPlay loop muted playsInline>
                <source src={file.url} type={`video/${fileExt}`} />
            </StyledVideo>
        );
    }

    if (isImage) {
        return <StyledImage preview={{ visible: false, mask: null }} src={file.url} />;
    }

    return t('bannerList:noData.previewNotSupported');
};

const BannerList = () => {
    const { t } = useTranslation('bannerList');

    const inputRef = useRef<InputRef>(null);

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Banner);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, filter, sort } = state;

    const { data, loading } = useListBannersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            filter: { ...filter, companyIds: company ? [company.id] : filter.companyIds },
            sort: pick(['field', 'order'], sort),
        },
    });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filter: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            bannerText: !filters.bannerText ? null : filters.bannerText[0],
                            moduleId: !filters.moduleId ? null : filters.moduleId[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const dataSource = useMemo(
        () => (data?.lists?.items || []).map(item => ({ ...item, key: item.id })),
        [data?.lists?.items]
    );

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.Banners });

    const total = data?.lists?.count || 0;

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const showSearchDropDown = useCallback(
        (props, dataIndex) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const navigate = useNavigate();

    const onRow = useCallback<PaginatedBannerTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('bannerList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={sort.field === BannerSortingField.Company ? sort.orderValue : undefined}
                    title={t('bannerList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="bannerImage"
                dataIndex="bannerImage"
                render={value => renderBannerPreview(t, value)}
                title={t('bannerList:columns.bannerImage')}
            />
            <Table.Column
                key="bannerText"
                dataIndex={['bannerText', 'defaultValue']}
                filterDropdown={props => showSearchDropDown(props, 'bannerText')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.bannerText ? [filter.bannerText] : undefined}
                sortOrder={sort.field === BannerSortingField.BannerText ? sort.orderValue : undefined}
                title={t('bannerList:columns.bannerText')}
                sorter
            />
            <Table.Column
                key="moduleDisplayName"
                dataIndex={['module', 'displayName']}
                sortOrder={sort.field === BannerSortingField.ModuleName ? sort.orderValue : undefined}
                title={t('bannerList:columns.module')}
                sorter
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('bannerList:columns.isActive')}
                width={120}
            />
        </PaginatedTableWithContext>
    );
};

export default BannerList;
